import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { Dictionary, groupBy } from "lodash";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { ISpiff } from "@/spiffs/spiffs.d";
import { getEnrichedSpiff } from "@/spiffs/utils";
import { useGetBusinessUsers } from "@/user/query";
import { ISpiffApproval, EnrichedSpiffApproval } from "./spiff-approval";
import { enrichSpiffApproval } from "./utils";

export const spiffApprovalsKey = (businessId?: string | null) =>
  ["spiff-approvals", businessId] as const;
export const spiffApprovalSpiffsKey = (spiffApprovalId?: string | null) =>
  ["spiff-approvals-spiffs", spiffApprovalId] as const;

export const useGetSpiffApprovals = (
  businessId?: string | null,
  select?: (data: ISpiffApproval[]) => EnrichedSpiffApproval[],
) => {
  const { get } = useApi();
  return useQuery({
    queryKey: spiffApprovalsKey(businessId),
    queryFn: () =>
      get(`businesses/${businessId}/spiff_approvals`).then((res) => res.data),
    enabled: !!businessId,
    select,
  });
};

export const useGetEnrichedSpiffApprovals = (
  businessId?: string | null,
): UseQueryResult<EnrichedSpiffApproval[], Error> => {
  const { data: businessUsers = [] } = useGetBusinessUsers(businessId);
  return useGetSpiffApprovals(
    businessId,
    useCallback(
      (data: ISpiffApproval[]) =>
        data.map((approval: ISpiffApproval) =>
          enrichSpiffApproval(approval, businessUsers),
        ),
      [businessUsers],
    ),
  );
};

export const useGetSpiffApprovalSpiffs = <TData = Dictionary<ISpiff[]>>(
  spiffApprovalId?: string | null,
  select?: (data: Dictionary<ISpiff[]>) => TData,
) => {
  const { get } = useApi();
  return useQuery({
    queryKey: spiffApprovalSpiffsKey(spiffApprovalId),
    queryFn: () =>
      get(`spiff_approvals/${spiffApprovalId}/spiffs`)
        .then((res) => res.data.map(getEnrichedSpiff))
        .then((spiffs) => groupBy(spiffs, "user_id")),
    enabled: !!spiffApprovalId,
    select,
  });
};

export const useGetApprovalUserSpiffs = (
  spiffApprovalId?: string | null,
  userId?: string | null,
): UseQueryResult<ISpiff[] | undefined, Error> =>
  useGetSpiffApprovalSpiffs(
    spiffApprovalId,
    useCallback(
      (data: Dictionary<ISpiff[]>) => (userId ? data[userId] : undefined),
      [userId],
    ),
  );

export const useBulkUpdateSpiffApprovals = (businessId?: string | null) => {
  const { patch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    { updated_count: number; spiff_approvals: ISpiffApproval[] },
    Error,
    { spiffApprovalIds: string[]; status: string }
  >({
    mutationFn: ({ spiffApprovalIds, status }) =>
      patch(`businesses/${businessId}/spiff_approvals/bulk_update`, {
        spiff_approval_ids: spiffApprovalIds,
        status,
      }).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: spiffApprovalsKey(businessId),
      });
    },
  });
};
